/**
 * Sanitization utilities for user-provided content
 */

/**
 * Validates and sanitizes a URL to prevent XSS attacks.
 * Only allows http/https protocols and valid URL format.
 * Returns null if the URL is invalid or potentially malicious.
 */
export function sanitizeUrl(url: string | null | undefined): string | null {
  if (!url || typeof url !== 'string') {
    return null;
  }

  // Trim whitespace
  const trimmedUrl = url.trim();
  
  if (!trimmedUrl) {
    return null;
  }

  try {
    const parsed = new URL(trimmedUrl);
    
    // Only allow http and https protocols
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      console.warn('Invalid URL protocol:', parsed.protocol);
      return null;
    }

    // Return the sanitized URL (this also normalizes it)
    return parsed.href;
  } catch {
    console.warn('Invalid URL format:', trimmedUrl);
    return null;
  }
}

/**
 * Validates a font URL - must be a valid URL with allowed font extensions.
 * Returns null if invalid.
 */
export function sanitizeFontUrl(url: string | null | undefined): string | null {
  const sanitized = sanitizeUrl(url);
  
  if (!sanitized) {
    return null;
  }

  // Check for allowed font file extensions
  const allowedExtensions = ['.woff', '.woff2', '.ttf', '.otf', '.eot'];
  const urlPath = new URL(sanitized).pathname.toLowerCase();
  
  const hasValidExtension = allowedExtensions.some(ext => urlPath.endsWith(ext));
  
  if (!hasValidExtension) {
    // Also allow Supabase storage URLs that don't have extensions
    // These are served with proper content-type headers
    if (sanitized.includes('supabase.co/storage/')) {
      return sanitized;
    }
    console.warn('Font URL does not have a valid font extension:', urlPath);
    return null;
  }

  return sanitized;
}

/**
 * Escapes characters that could break out of CSS url() context
 */
export function escapeCssUrl(url: string): string {
  // Replace characters that could break out of CSS context
  return url
    .replace(/\\/g, '\\\\')
    .replace(/'/g, "\\'")
    .replace(/"/g, '\\"')
    .replace(/\)/g, '\\)')
    .replace(/\(/g, '\\(')
    .replace(/\n/g, '')
    .replace(/\r/g, '');
}

/**
 * Builds a safe @font-face CSS rule from a validated font URL
 */
export function buildSafeFontFaceStyle(fontUrl: string | null | undefined): string {
  const sanitizedUrl = sanitizeFontUrl(fontUrl);
  
  if (!sanitizedUrl) {
    return '';
  }

  const escapedUrl = escapeCssUrl(sanitizedUrl);

  return `
    @font-face {
      font-family: 'CustomFont';
      src: url('${escapedUrl}') format('woff2'), url('${escapedUrl}') format('woff'), url('${escapedUrl}') format('truetype');
      font-weight: normal;
      font-style: normal;
      font-display: swap;
    }
  `;
}
